import { Injectable } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { combineLatest, EMPTY, Observable, of, throwError } from 'rxjs'
import { catchError, concatMap, last, map, switchMap, toArray } from 'rxjs/operators'
import { Message, MessageType } from '@tv3/store/message/message.model'
import { ChannelMessagePayload, MessageRequest } from '@tv3/store/message/message.request'
import { MessageResponse } from '@tv3/interfaces/responses/message.response'
import { deserializeArray, isSomething, isUUIDLose, Toaster } from '@tokeet-frontend/tv3-platform'
import * as lodash from 'lodash'
import * as moment from 'moment'

@Injectable({
  providedIn: 'root',
})
export class MessageService {
  constructor(private http: HttpClient, private toaster: Toaster) {}

  getMessagesByInquiryId(inquiryId: string, background = false): Observable<Message[]> {
    const url = `@api/message/inquiry/${inquiryId}?include_draft=1`
    return this.http
      .get(url, { headers: background ? { ignoreProgressBar: 'true' } : {} })
      .pipe(deserializeArray<Message>(Message))
  }

  // maxPages bounds the recursion: pages are newest-first, so a cap keeps the most recent
  // maxPages*limit messages and drops only the oldest tail of the window.
  // preview=1 asks the API for snippet-only messages (no message_html); the feed and
  // dashboard render truncated text, and thread views replace these entries with full
  // copies via GetBookingMessagesComplete when an inquiry opens
  getLatest(type?: MessageType, lastDays = 90, limit = 1000, skip = 0, maxPages = 10): Observable<Message[]> {
    const url = `@api/message/latest`
    const created = moment().subtract(lastDays, 'day').startOf('day').unix()
    return this.http.get(url, { params: { type: type as any, limit, skip, created, preview: 1 } }).pipe(
      deserializeArray<Message>(Message),
      switchMap((messages) => {
        if (messages.length < limit || maxPages <= 1) {
          return of(messages)
        }
        return this.getLatest(type, lastDays, limit, skip + limit, maxPages - 1).pipe(
          map((messages2) => [...messages, ...messages2])
        )
      })
    )
  }

  // delta poll: only messages created after the given epoch, newest first; pages until a
  // short page so bursts larger than `limit` aren't dropped, bounded by maxPages so a
  // huge backlog can't recurse forever
  getNewMessagesSince(created: number, limit = 100, skip = 0, maxPages = 5): Observable<Message[]> {
    const url = `@api/message/latest`
    return this.http.get(url, { params: { limit, skip, created } as any, headers: { ignoreProgressBar: 'true' } }).pipe(
      deserializeArray<Message>(Message),
      switchMap((messages) => {
        if (messages.length < limit || maxPages <= 1) {
          return of(messages)
        }
        return this.getNewMessagesSince(created, limit, skip + limit, maxPages - 1).pipe(
          map((more) => [...messages, ...more])
        )
      })
    )
  }

  getLatestByTypes(types: MessageType[], lastDays?: number, maxPages?: number): Observable<Message[]> {
    return combineLatest(types.map((type) => this.getLatest(type, lastDays, undefined, 0, maxPages))).pipe(
      map((messages) => lodash.flatten(messages))
    )
  }

  getMessagesByGuestId(guestId: string, limit = 200, skip = 0, background = false) {
    const url = `@api/guest/emails/${guestId}?limit=${limit}&skip=${skip}&include_draft=1`
    return this.http
      .get(url, { headers: background ? { ignoreProgressBar: 'true' } : {} })
      .pipe(deserializeArray<Message>(Message))
  }

  getMessagesByIds(messageIds: string[]) {
    if (!isSomething(messageIds)) return of([])

    const url = `@api/message/selected/`
    return this.http.put(url, { messages: messageIds }).pipe(deserializeArray<Message>(Message))
  }

  create(request: MessageRequest): Observable<Message> {
    const url = `@api/message/`

    return this.http.post<object>(url, request).pipe(
      // when sending message to ABB, no message returned, create one from payload
      map((response) => Message.deserialize(response))
    )
  }

  update(msgId: string, payload: any): Observable<Message> {
    const url = `@api/message/update/${msgId}`
    return this.http.put<object>(url, payload).pipe(map((response) => Message.deserialize(response as any)))
  }

  createChannelMessage(payload: ChannelMessagePayload) {
    const url = '@api/v1/message'
    return this.http.post<any>(url, payload).pipe(
      catchError((response) => {
        if (response.error?.error === 'account is not allowed to send message') {
          return throwError(new Error($localize`Please reconnect your Gmail account and try again.`))
        }
        if (response.status === 400 && response.error?.error === 'phone number is invalid') {
          return throwError(new Error($localize`Guest doesn't have a valid phone number.`))
        }
        return throwError(response)
      })
    )
  }

  remove(messageId: string) {
    const url = `@api/message/delete/${messageId}`

    return this.http.delete<MessageResponse>(url)
  }

  removeBatch(messageIds: string[]) {
    const url = `@api/message/delete/batch`

    return this.http.put(url, { ids: messageIds })
  }

  removeFromFeed(message: Message) {
    const url = `@api/message/remove/feed/${message.id}`

    return this.http.delete<MessageResponse>(url)
  }

  removeFromFeedBatch(messages: Message[]) {
    return of(...messages).pipe(
      concatMap((item) => this.removeFromFeed(item)),
      toArray()
    )
  }

  markAsRead(messageIds: string[]) {
    const url = `@api/message/read/all`

    messageIds = lodash.filter(messageIds, (id) => isUUIDLose(id))

    return this.http.put(url, { messages: messageIds })
  }

  markAsUnread(messageIds: string[]) {
    const url = `@api/message/unread/all`

    messageIds = lodash.filter(messageIds, (id) => isUUIDLose(id))

    return this.http.put(url, { messages: messageIds })
  }

  getUnreadCount(): Observable<number> {
    const url = `@api/message/unread/count`
    return this.http.get<{ count: number }>(url).pipe(map((res) => res.count))
  }

  move(messageId: string, inquiryId: string) {
    const url = `@api/message/move/${messageId}/${inquiryId}`

    return this.http.put(url, {})
  }

  // move messages in a conversation to a different inquiry
  moveMessagesInConversation(convoId: string, inquiryId: string, guestId?: string) {
    const url = `@api/message/move/convo/${convoId}/${inquiryId}`

    return this.http.put<{ updated: MessageResponse[] }>(url, { guest_id: guestId }).pipe(
      map(({ updated }) => updated),
      deserializeArray<Message>(Message)
    )
  }
}
